/*
 * Copyright (c) 2021 by Leyton Group - All Rights Reserved.
 *  This source code, and resulting software, is the confidential and proprietary information
 *  ("Proprietary Information") and is the intellectual property ("Intellectual Property")
 *  of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
 *  and shall use it only in accordance with the terms and conditions of any and all license
 *  agreements you have entered into with The Company.
 */

import Vue from "vue";
import axios from "axios";
import { loginService } from "./views/Auth/services";
// import router from './router/Routes';
import { router } from "./plugins/vue-router";
import store from "./store";
import { cookieUtils } from "./shared/cookie.util";
import { constants } from "@/shared/constants";

const CONTACTS_CRAFT_API = "/api/contacts-craft/email-status";
const SIRET_ATTACHED_TO_COMPANY_API = "/api/simulation/siretAttachedToCompany";
const FIND_BENEFICIARY_API = "/api/beneficiary/findBySiren";
const FIND_SIRETS_API = "/api/siret/getSirets";
const FIND_FICHES_API = "/api/fiche/with-mapping";
const FIND_MAPPINGS_API = "/api/import/with-mapping";
const PREVIEW_OPERATIONS_API = "/api/import/importPreview";
const DOWNLOAD_MAPPING_API = "/api/import/download-mapping-file";
const FETCH_IMPORT_BY_ID = "/api/import/fetch-import-by-id";
const FETCH_ESTIMATION_BY_GLOBAL_OPERATION_ID_API =
  "/api/simulation/estimations/globalOperations";
const WORKS_NOTIFICATIONS_API = "/api/works/notification";
const DASHBORD_NOTIFICATIONS_API = "/api/notifications/dashboard";

const APIS_To_HIDE_LOADER = [
  CONTACTS_CRAFT_API,
  SIRET_ATTACHED_TO_COMPANY_API,
  FIND_BENEFICIARY_API,
  FIND_SIRETS_API,
  FIND_FICHES_API,
  FIND_MAPPINGS_API,
  PREVIEW_OPERATIONS_API,
  DOWNLOAD_MAPPING_API,
  FETCH_IMPORT_BY_ID,
  FETCH_ESTIMATION_BY_GLOBAL_OPERATION_ID_API,
  WORKS_NOTIFICATIONS_API,
  DASHBORD_NOTIFICATIONS_API
];

// request interceptor
axios.interceptors.request.use(
  config =>
    new Promise((resolve, reject) => {
      const urlRequet = config.url;
      const hideLoaderIf = APIS_To_HIDE_LOADER.some(api =>
        urlRequet.includes(api)
      );
      if (!hideLoaderIf) {
        pending(config.showLoader);
      }
      if (config.url && !config.headers.publicService) {
        if (!cookieUtils.doesJWTCookieExist("Bearer")) {
          if (!cookieUtils.doesJWTCookieExist("Refresh")) {
            logOut();
          } else {
            loginService.refreshToken().then(() => {
              resolve(config);
            }, reject);
          }
        } else {
          resolve(config);
        }
      } else {
        resolve(config);
      }
    })
);

// response interceptor
axios.interceptors.response.use(
  response => {
    done(response.config.showLoader);
    return response;
  },
  error => {
    if (error.message === "cancel") {
      done(true);
    } else {
      done(error.response && error.response.config.showLoader);
    }
    let message;
    if (!error.response) {
      return;
    }
    const BAD_REQUEST_ERROR = 400;
    if (
      error.response.status &&
      error.response.status === BAD_REQUEST_ERROR &&
      error.response.data
    ) {
      if (error.response.data instanceof Blob) {
        processBlobErrorResponse(error);
      } else {
        if (error.response.data.errorCode === constants.ERROR_CODE.GWBE00012) {
          Vue.prototype.$notification.error(error.response.data.message);
          // store.commit("simulation/setGoNextStepDisabled", true);
        } else if (
          error.response.data.errorCode === constants.ERROR_CODE.GWBE00016
        ) {
          console.error(error.response.data?.message);
        } else if (
          error.response.data.errorCode === constants.ERROR_CODE.GWBE00013
        ) {
          Vue.prototype.$notification.warning(error.response.data?.message);
        } else {
          Vue.prototype.$notification.error(error.response.data?.message);
        }
      }
    } else {
      const UNAUTHORIZED_ERROR = 401;
      if (
        error.response.status &&
        error.response.status === UNAUTHORIZED_ERROR
      ) {
        if (!error.response.config.headers.publicService) {
          logOut();
        }
        Vue.prototype.$notification.error(error.response.data?.detail);
      } else {
        message = `${error.response.data.detail}`;
      }
    }
    // this.$notification.error(message);
    return Promise.reject(error);
  }
);

function logOut() {
  store.commit("account/logout");
  if (window._paq) {
    window._paq.push(["setUserId", undefined]);
  }
}

function pending(showLoader) {
  if (showLoader) {
    store.commit("loader/pending");
  }
}

function done(showLoader) {
  if (showLoader) {
    store.commit("loader/done");
  }
}

function processBlobErrorResponse(error) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      error.response.data = JSON.parse(reader.result);
      Vue.prototype.$notification.error(error.response.data.message);
      resolve(Promise.reject(error));
    };
    reader.onerror = () => {
      reject(error);
    };
    reader.readAsText(error.response.data);
  });
}

export default axios;
